import React, { useState, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ZoomIn, ZoomOut, Move, Crosshair, Eye, EyeOff, RotateCcw, Layers, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Save, Magnet, Ruler, Sparkles, Tag, Diff } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useBlobImage } from "@/hooks/useBlobImage";
import { useTintedImage } from "@/hooks/useTintedImage";
import api from "@/lib/api";

const LAYERS = {
  concessione: { label: "Concessione", color: "#FF3366", rgb: [255, 51, 102] },
  catastale: { label: "Catasto", color: "#FFD600", rgb: [255, 214, 0] },
};
const DEFAULT_T = { x: 0, y: 0, scale: 1, rot: 0, opacity: 70, visible: true };
const fromAlignment = (al) => ({
  concessione: { ...DEFAULT_T, ...(al?.concessione || {}) },
  catastale: { ...DEFAULT_T, ...(al?.catastale || {}) },
});

const IconBtn = ({ onClick, children, testid, title, active }) => (
  <button data-testid={testid} title={title} onClick={onClick}
    className={`h-9 w-9 rounded-lg border flex items-center justify-center transition-colors ${active ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/10" : "bg-[#1A2332] border-slate-700 text-slate-300 hover:text-cyan-300 hover:border-cyan-500/50"}`}>
    {children}
  </button>
);

const NudgeBtn = ({ testid, onClick, children }) => (
  <button data-testid={testid} onClick={(e) => { e.stopPropagation(); onClick(); }} className="h-6 w-6 rounded bg-[#1A2332] border border-slate-700 flex items-center justify-center hover:border-cyan-500/50">{children}</button>
);

const LayerControls = ({ id, t, active, onSelect, onChange, onReset }) => {
  const L = LAYERS[id];
  const set = (patch) => onChange({ ...t, ...patch });
  const nudge = (dx, dy) => set({ x: t.x + dx, y: t.y + dy });
  const opId = id === "catastale" ? "catasto" : id;
  return (
    <div data-testid={`layer-panel-${id}`} onClick={onSelect}
      className={`rounded-lg p-3 border cursor-pointer transition-colors ${active ? "border-cyan-500/60 bg-cyan-500/[0.04]" : "border-slate-800 bg-[#0B0F17]"}`}>
      <div className="flex items-center justify-between mb-2">
        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
          <input type="radio" data-testid={`layer-select-${id}`} checked={active} onChange={onSelect} className="accent-cyan-400" />
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: L.color }} /> {L.label}
          {active && <span className="font-mono-t text-[10px] text-cyan-400 uppercase">attivo</span>}
        </label>
        <div className="flex items-center gap-2">
          <button data-testid={`layer-reset-${id}`} title="Reimposta" onClick={(e) => { e.stopPropagation(); onReset(); }} className="text-slate-400 hover:text-cyan-300"><RotateCcw className="h-4 w-4" /></button>
          <button data-testid={`overlay-toggle-${opId}`} onClick={(e) => { e.stopPropagation(); set({ visible: !t.visible }); }} className="text-slate-400 hover:text-cyan-300">
            {t.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <span className="w-14">Scala</span>
          <Slider data-testid={`scale-slider-${id}`} value={[t.scale * 100]} onValueChange={([v]) => set({ scale: v / 100 })} min={20} max={300} step={1} className="flex-1" />
          <span data-testid={`scale-value-${id}`} className="font-mono-t w-12 text-right text-slate-300">{Math.round(t.scale * 100)}%</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <span className="w-14">Rotazione</span>
          <Slider data-testid={`rotation-slider-${id}`} value={[t.rot]} onValueChange={([v]) => set({ rot: v })} min={-180} max={180} step={0.5} className="flex-1" />
          <span data-testid={`rotation-value-${id}`} className="font-mono-t w-12 text-right text-slate-300">{t.rot.toFixed(1)}°</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <span className="w-14">Opacità</span>
          <Slider data-testid={`opacity-slider-${opId}`} value={[t.opacity]} onValueChange={([v]) => set({ opacity: v })} max={100} step={1} className="flex-1" />
          <span className="font-mono-t w-12 text-right text-slate-300">{t.opacity}%</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <span className="w-14">Posizione</span>
          <div className="flex gap-1">
            <NudgeBtn testid={`nudge-left-${id}`} onClick={() => nudge(-1, 0)}><ArrowLeft className="h-3 w-3" /></NudgeBtn>
            <NudgeBtn testid={`nudge-up-${id}`} onClick={() => nudge(0, -1)}><ArrowUp className="h-3 w-3" /></NudgeBtn>
            <NudgeBtn testid={`nudge-down-${id}`} onClick={() => nudge(0, 1)}><ArrowDown className="h-3 w-3" /></NudgeBtn>
            <NudgeBtn testid={`nudge-right-${id}`} onClick={() => nudge(1, 0)}><ArrowRight className="h-3 w-3" /></NudgeBtn>
          </div>
          <span data-testid={`offset-value-${id}`} className="font-mono-t text-slate-400 ml-auto">x {Math.round(t.x)} · y {Math.round(t.y)}</span>
        </div>
      </div>
    </div>
  );
};

export const OverlayEngine = ({ praticaId, concessionePath, catastalePath, alignment, calibration, quote, regioni }) => {
  const qc = useQueryClient();
  const [showQuote, setShowQuote] = useState(true);
  const [showDiff, setShowDiff] = useState(true);
  const [diffOpacity, setDiffOpacity] = useState(95);
  const [imgSize, setImgSize] = useState({ concessione: null, catastale: null });
  const quoteMut = useMutation({
    mutationFn: async (tipo) => (await api.post(`/pratiche/${praticaId}/quote`, { tipo })).data,
    onSuccess: (q, tipo) => { qc.invalidateQueries({ queryKey: ["pratica", praticaId] }); setShowQuote(true); toast.success(`${q.muri.length} muri quotati su ${LAYERS[tipo].label}`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Rilevamento AI non riuscito"),
  });
  const concRaw = useBlobImage(concessionePath);
  const catRaw = useBlobImage(catastalePath);
  const urls = {
    concessione: useTintedImage(concRaw, LAYERS.concessione.rgb),
    catastale: useTintedImage(catRaw, LAYERS.catastale.rgb),
  };

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [active, setActive] = useState("concessione");
  const [layers, setLayers] = useState(() => fromAlignment(alignment));
  const [dirty, setDirty] = useState(false);
  const [mode, setMode] = useState("layer"); // layer | view | measure
  const [measure, setMeasure] = useState([]);

  const viewportRef = useRef(null);
  const imgRefs = { concessione: useRef(null), catastale: useRef(null) };
  const drag = useRef(null);

  useEffect(() => { if (alignment && !dirty) setLayers(fromAlignment(alignment)); }, [alignment, dirty]);

  const setLayer = (id, t) => { setDirty(true); setLayers((s) => ({ ...s, [id]: t })); };
  const patchLayer = (id, fn) => { setDirty(true); setLayers((s) => ({ ...s, [id]: { ...s[id], ...fn(s[id]) } })); };

  const onDown = (e) => {
    if (mode === "measure") {
      const r = viewportRef.current.getBoundingClientRect();
      const pt = { x: e.clientX - r.left, y: e.clientY - r.top };
      setMeasure((m) => (m.length >= 2 ? [pt] : [...m, pt]));
      return;
    }
    drag.current = { x: e.clientX, y: e.clientY, view: mode === "view" || e.shiftKey };
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x, dy = e.clientY - drag.current.y;
    drag.current = { ...drag.current, x: e.clientX, y: e.clientY };
    if (drag.current.view) setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
    else patchLayer(active, (t) => ({ x: t.x + dx / zoom, y: t.y + dy / zoom }));
  };
  const onUp = () => { drag.current = null; };
  const onWheel = (e) => {
    if (!e.ctrlKey && !e.altKey) return;
    e.preventDefault();
    const f = e.deltaY < 0 ? 1.02 : 0.98;
    patchLayer(active, (t) => ({ scale: Math.min(3, Math.max(0.2, t.scale * f)) }));
  };
  const resetAll = () => { setZoom(1); setPan({ x: 0, y: 0 }); setDirty(true); setLayers({ concessione: { ...DEFAULT_T }, catastale: { ...DEFAULT_T } }); setMeasure([]); };

  // px visualizzati (a zoom 1, scala 1) per cm su carta, per layer calibrato
  const dispPxPerCm = (id) => {
    const cal = calibration?.[id];
    const img = imgRefs[id].current;
    if (!cal?.px_per_cm || !img?.clientWidth) return null;
    const natW = cal.image_width || img.naturalWidth;
    return cal.px_per_cm * (img.clientWidth / natW);
  };

  const autoSnap = () => {
    const c = dispPxPerCm("concessione"), k = dispPxPerCm("catastale");
    if (!c || !k) { toast.error("Calibra il righello su entrambe le planimetrie prima dell'aggancio automatico"); return; }
    const ratio = c / k;
    setDirty(true);
    setLayers((s) => ({
      concessione: { ...s.concessione, x: 0, y: 0, rot: 0, scale: 1 },
      catastale: { ...s.catastale, x: 0, y: 0, rot: 0, scale: Math.min(3, Math.max(0.2, ratio)) },
    }));
    setPan({ x: 0, y: 0 }); setZoom(1);
    toast.success(`Planimetrie agganciate alla stessa scala (catasto ${Math.round(ratio * 100)}%)`);
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const r = viewportRef.current.getBoundingClientRect();
      const pick = ({ x, y, scale, rot, opacity }) => ({ x, y, scale, rot, opacity });
      return (await api.put(`/pratiche/${praticaId}/alignment`, { frame_w: r.width, frame_h: r.height, concessione: pick(layers.concessione), catastale: pick(layers.catastale) })).data;
    },
    onSuccess: () => { setDirty(false); qc.invalidateQueries({ queryKey: ["pratica", praticaId] }); toast.success("Allineamento salvato: il report PDF userà questa sovrapposizione"); },
    onError: () => toast.error("Errore nel salvataggio dell'allineamento"),
  });

  // misura reale sul layer attivo
  let measureText = null;
  if (measure.length === 2) {
    const dPx = Math.hypot(measure[1].x - measure[0].x, measure[1].y - measure[0].y);
    const ppc = dispPxPerCm(active);
    if (ppc) {
      const cm = dPx / (zoom * layers[active].scale * ppc);
      measureText = `${cm.toFixed(2)} m reali (${cm.toFixed(1)} cm su carta 1:100) · ${LAYERS[active].label}`;
    } else measureText = `${dPx.toFixed(0)} px · calibra il righello su ${LAYERS[active].label} per la misura in metri`;
  }

  // lunghezza muro in metri: calibrazione righello se presente, altrimenti stima AI
  const wallLength = (id, m) => {
    const cal = calibration?.[id];
    const img = imgRefs[id].current;
    if (cal?.px_per_cm && img) {
      const natW = cal.image_width || img.naturalWidth, natH = cal.image_height || img.naturalHeight;
      const px = Math.hypot((m.x2 - m.x1) * natW, (m.y2 - m.y1) * natH);
      return { m: px / cal.px_per_cm, src: "righello" };
    }
    return { m: m.lunghezza_m, src: "AI" };
  };

  const anyPath = concessionePath || catastalePath;
  const anyUrl = urls.concessione || urls.catastale;
  const modeLabel = mode === "layer" ? `Trascina: ${LAYERS[active].label}` : mode === "view" ? "Trascina: vista" : "Misura: 2 click";

  return (
    <div className="rounded-xl border border-slate-800 bg-[#121824] overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Crosshair className="h-4 w-4 text-cyan-400" />
          <span className="font-heading font-semibold text-sm">Motore di Sovrapposizione · Scala 1:100</span>
          {dirty && <span data-testid="alignment-dirty-badge" className="font-mono-t text-[10px] text-amber-400 uppercase">non salvato</span>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button data-testid="drag-mode-toggle" onClick={() => { setMode((m) => (m === "layer" ? "view" : "layer")); setMeasure([]); }}
            className={`h-9 px-3 rounded-lg border text-xs inline-flex items-center gap-1.5 ${mode !== "measure" ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/10" : "border-slate-700 text-slate-300 bg-[#1A2332]"}`}>
            {mode === "view" ? <Move className="h-4 w-4" /> : <Layers className="h-4 w-4" />}
            {modeLabel}
          </button>
          <IconBtn testid="measure-mode-toggle" title="Misura reale (metri)" active={mode === "measure"} onClick={() => { setMode((m) => (m === "measure" ? "layer" : "measure")); setMeasure([]); }}><Ruler className="h-4 w-4" /></IconBtn>
          <button data-testid="detect-quote-button" disabled={quoteMut.isPending || !urls[active]} onClick={() => quoteMut.mutate(active)} title="Rileva muri principali con AI e quota in metri"
            className="h-9 px-3 rounded-lg border border-slate-700 bg-[#1A2332] text-xs text-slate-300 inline-flex items-center gap-1.5 hover:border-cyan-500/50 disabled:opacity-40">
            <Sparkles className={`h-4 w-4 ${quoteMut.isPending ? "animate-pulse text-cyan-300" : ""}`} /> {quoteMut.isPending ? "Rilevamento…" : "Quote AI"}
          </button>
          <IconBtn testid="toggle-quote-button" title="Mostra/nascondi quote" active={showQuote} onClick={() => setShowQuote((v) => !v)}><Tag className="h-4 w-4" /></IconBtn>
          <IconBtn testid="toggle-diff-button" title="Riquadri difformità AI" active={showDiff} onClick={() => setShowDiff((v) => !v)}><Diff className="h-4 w-4" /></IconBtn>
          <IconBtn testid="auto-snap-button" title="Aggancio automatico da calibrazione" onClick={autoSnap}><Magnet className="h-4 w-4" /></IconBtn>
          <IconBtn testid="zoom-out-button" title="Zoom -" onClick={() => setZoom((z) => Math.max(0.3, z - 0.15))}><ZoomOut className="h-4 w-4" /></IconBtn>
          <span className="font-mono-t text-xs text-slate-400 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <IconBtn testid="zoom-in-button" title="Zoom +" onClick={() => setZoom((z) => Math.min(4, z + 0.15))}><ZoomIn className="h-4 w-4" /></IconBtn>
          <IconBtn testid="reset-view-button" title="Reimposta tutto" onClick={resetAll}><RotateCcw className="h-4 w-4" /></IconBtn>
          <button data-testid="save-alignment-button" disabled={saveMut.isPending || !anyPath} onClick={() => saveMut.mutate()}
            className="h-9 px-3 rounded-lg bg-cyan-500 text-slate-900 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-cyan-400 disabled:opacity-40">
            <Save className="h-4 w-4" /> Salva allineamento
          </button>
        </div>
      </div>

      <div
        ref={viewportRef}
        data-testid="cad-viewport-container"
        className={`cad-canvas relative h-[460px] overflow-hidden select-none ${mode === "measure" ? "cursor-crosshair" : "cursor-grab active:cursor-grabbing"}`}
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} onWheel={onWheel}
      >
        <div className="absolute inset-0 flex items-center justify-center"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "center" }}>
          {["catastale", "concessione"].map((id) => {
            const t = layers[id];
            const tf = `translate(${t.x}px, ${t.y}px) rotate(${t.rot}deg) scale(${t.scale})`;
            const muri = showQuote && t.visible ? quote?.[id]?.muri || [] : [];
            const sz = imgSize[id];
            return urls[id] ? (
              <React.Fragment key={id}>
                <img ref={imgRefs[id]} src={urls[id]} alt={LAYERS[id].label} data-testid={`overlay-layer-${id}`}
                  onLoad={(e) => setImgSize((s) => ({ ...s, [id]: { w: e.target.clientWidth, h: e.target.clientHeight } }))}
                  className={`absolute max-w-[80%] max-h-[90%] object-contain pointer-events-none ${active === id ? "drop-shadow-[0_0_6px_rgba(34,211,238,0.35)]" : ""}`}
                  style={{ opacity: t.visible ? t.opacity / 100 : 0, visibility: t.visible ? "visible" : "hidden", transform: tf }} />
                {sz && muri.length > 0 && (
                  <svg data-testid={`quote-layer-${id}`} className="absolute pointer-events-none overflow-visible" width={sz.w} height={sz.h} style={{ transform: tf }}>
                    {muri.map((m, i) => {
                      const x1 = m.x1 * sz.w, y1 = m.y1 * sz.h, x2 = m.x2 * sz.w, y2 = m.y2 * sz.h;
                      const len = wallLength(id, m);
                      const ang = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
                      const flip = ang > 90 || ang < -90 ? 180 : 0;
                      return (
                        <g key={i}>
                          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={LAYERS[id].color} strokeWidth="1.5" strokeDasharray="4 3" opacity="0.9" />
                          <circle cx={x1} cy={y1} r="2.5" fill={LAYERS[id].color} /><circle cx={x2} cy={y2} r="2.5" fill={LAYERS[id].color} />
                          <g transform={`translate(${(x1 + x2) / 2} ${(y1 + y2) / 2}) rotate(${ang + flip})`}>
                            <rect x="-24" y="-16" width="48" height="13" rx="2" fill="#0B0F17" stroke={LAYERS[id].color} strokeWidth="0.75" />
                            <text data-testid={`quote-label-${id}-${i}`} x="0" y="-6.5" textAnchor="middle" fontSize="8" fontFamily="JetBrains Mono, monospace" fill="#E2E8F0">{len.m.toFixed(2)} m</text>
                          </g>
                        </g>
                      );
                    })}
                  </svg>
                )}
              </React.Fragment>
            ) : null;
          })}
          {!anyPath && <p className="text-slate-600 text-sm">Carica le planimetrie per visualizzare la sovrapposizione</p>}
          {anyPath && !anyUrl && <p className="text-slate-500 text-sm">Elaborazione layer in corso…</p>}
          {showDiff && regioni?.length > 0 && imgSize.concessione && layers.concessione.visible && (
            <svg data-testid="ai-regions-layer" className="absolute pointer-events-none overflow-visible" width={imgSize.concessione.w} height={imgSize.concessione.h}
              style={{ transform: `translate(${layers.concessione.x}px, ${layers.concessione.y}px) rotate(${layers.concessione.rot}deg) scale(${layers.concessione.scale})`, opacity: diffOpacity / 100 }}>
              {regioni.map((r, i) => {
                const w = imgSize.concessione.w, h = imgSize.concessione.h;
                const x = Math.min(r.x1, r.x2) * w, y = Math.min(r.y1, r.y2) * h, rw = Math.abs(r.x2 - r.x1) * w, rh = Math.abs(r.y2 - r.y1) * h;
                return (
                  <g key={i} data-testid={`ai-region-${i}`}>
                    <rect x={x} y={y} width={rw} height={rh} fill="rgba(255,255,255,0.06)" stroke="#FFFFFF" strokeWidth="3" strokeDasharray="8 4" rx="3" />
                    <rect x={x} y={y - 15} width={Math.max(40, (r.label || "").length * 5.6 + 10)} height="14" fill="#FFFFFF" rx="2" />
                    <text x={x + 5} y={y - 4.5} fontSize="8.5" fontFamily="JetBrains Mono, monospace" fontWeight="700" fill="#0B0F17">{i + 1}. {r.label}</text>
                  </g>
                );
              })}
            </svg>
          )}
        </div>

        {measure.length > 0 && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            {measure.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="5" fill="#22D3EE" stroke="#0B0F17" strokeWidth="2" />)}
            {measure.length === 2 && <line x1={measure[0].x} y1={measure[0].y} x2={measure[1].x} y2={measure[1].y} stroke="#22D3EE" strokeWidth="2" strokeDasharray="6 3" />}
          </svg>
        )}
        {measureText && (
          <div data-testid="measure-result" className="absolute top-3 left-3 px-3 py-1.5 rounded-lg bg-cyan-500/90 text-slate-900 text-xs font-semibold font-mono-t">{measureText}</div>
        )}

        {quote && showQuote && (quote.concessione?.muri?.length || quote.catastale?.muri?.length) ? (
          <div data-testid="quote-legend" className="absolute top-3 right-3 px-3 py-1.5 rounded-lg bg-black/50 backdrop-blur border border-white/10 text-[10px] text-slate-300 font-mono-t">
            Quote: {["concessione", "catastale"].filter((id) => quote[id]?.muri?.length).map((id) => `${LAYERS[id].label} ${quote[id].muri.length} muri (${calibration?.[id]?.px_per_cm ? "righello" : "stima AI"})`).join(" · ")}
          </div>
        ) : null}

        <div className="absolute bottom-3 left-3 flex items-center gap-3 px-3 py-1.5 rounded-lg bg-black/50 backdrop-blur border border-white/10">
          {Object.entries(LAYERS).map(([id, L]) => (
            <div key={id} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: L.color }} /><span className="text-[11px] text-slate-300">{L.label}</span></div>
          ))}
          {showDiff && regioni?.length > 0 && <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm border-2 border-dashed border-white" /><span className="text-[11px] text-slate-300">Difformità AI</span></div>}
        </div>
        <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-lg bg-black/50 backdrop-blur border border-white/10 text-[10px] text-slate-400 font-mono-t hidden sm:block">
          {mode === "measure" ? "Clicca 2 punti su un muro del layer attivo" : "Trascina = sposta layer attivo · Shift+trascina = vista · Ctrl+rotella = scala layer"}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 p-4 border-t border-slate-800">
        {Object.keys(LAYERS).map((id) => (
          <LayerControls key={id} id={id} t={layers[id]} active={active === id}
            onSelect={() => setActive(id)} onChange={(t) => setLayer(id, t)} onReset={() => setLayer(id, { ...DEFAULT_T })} />
        ))}
        <div data-testid="layer-panel-diff" className={`sm:col-span-2 rounded-lg p-3 border ${showDiff ? "border-slate-500/40 bg-white/[0.02]" : "border-slate-800 bg-[#0B0F17]"}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 text-xs text-slate-300">
              <Diff className="h-4 w-4 text-slate-200" /> Difformità rilevate dall'AI
              <span data-testid="ai-regions-count" className="font-mono-t text-[10px] text-slate-500">{regioni?.length || 0} riquadri</span>
            </div>
            <button data-testid="overlay-toggle-diff" onClick={() => setShowDiff((v) => !v)} className="text-slate-400 hover:text-white">
              {showDiff ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </button>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span className="w-14">Opacità</span>
            <Slider data-testid="opacity-slider-diff" value={[diffOpacity]} onValueChange={([v]) => setDiffOpacity(v)} max={100} step={1} className="flex-1" />
            <span className="font-mono-t w-12 text-right text-slate-300">{diffOpacity}%</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">{regioni?.length > 0 ? `${regioni.length} difformità perimetrate in bianco sulla concessione, numerate come nell'elenco dell'analisi.` : "Esegui l'analisi AI per perimetrare le difformità rilevate."}</p>
        </div>
      </div>
    </div>
  );
};
